/**
 * Device Tool - BIM 2025 Grade
 * 
 * Electrical device placement (Receptacles, Switches, Data, Fire Alarm) with:
 * - Host auto-detection (Wall, Floor, Face, Vertical Face)
 * - Gangable box support (1-gang, 2-gang, n-gang)
 * - Circuit connectors and electrical properties
 * - Annotation symbol integration
 * - Switch system logic
 */

import { v4 as uuidv4 } from 'uuid';
import { Element, Constraint } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

// ==================== Device Types ====================

export type DeviceCategory =
    | 'Electrical Fixtures'     // Receptacles
    | 'Light Switches'          // Switches
    | 'Data Devices'            // Ethernet, Phone
    | 'Fire Alarm Devices'      // Strobes, detectors
    | 'Communication Devices'   // Intercoms
    | 'Nurse Call Devices';     // Hospital systems

export type DeviceMounting =
    | 'Vertical Face'   // Wall
    | 'Face'            // Floor/Ceiling/Countertop
    | 'Work Plane';     // Generic plane

export type OutletType =
    | 'Duplex'
    | 'Quadplex'
    | 'Simplex'
    | 'GFCI'
    | 'USB'
    | '240V';

export type SwitchType =
    | 'Single Pole'
    | '3-Way'
    | '4-Way'
    | 'Dimmer'
    | 'Timer'
    | 'Sensor';

// ==================== Electrical Data ====================

export interface ElectricalConnector {
    id: string;
    systemType: 'Power' | 'Data' | 'Telephone' | 'Fire Alarm' | 'Controls';
    voltage: number;        // Volts (120, 208, 240, 277)
    phases: 1 | 2 | 3;
    load: number;           // VA (Apparent Power)
    loadClassification: string; // "Receptacle", "Lighting", "Motor"
    circuitId?: string;     // Assigned circuit
    panelId?: string;       // Assigned panel
}

// ==================== Enhanced Geometry ====================

export interface EnhancedDeviceGeometryData {
    // Positioning
    position: [number, number, number];
    rotation: number;       // degrees

    // Host
    hostId?: string;
    mounting: DeviceMounting;
    heightFromLevel: number;

    // Type info
    category: DeviceCategory;
    boxType: number;        // Gang count (1, 2, 3...)

    // Device specific
    outletType?: OutletType;
    switchType?: SwitchType;

    // Connections
    connectors: ElectricalConnector[];

    // Annotation
    symbolRotation: number; // degrees (for plan view)
}

// ==================== Enhanced Device Element ====================

export interface EnhancedDeviceElement extends Element {
    category: 'Electrical Fixtures' | 'Electrical Devices' | 'Lighting Devices'; // Mapped from DeviceCategory
    geometryData: EnhancedDeviceGeometryData;

    // Electrical Logic
    electricalData: {
        isCircuitLoad: boolean; // Does it consume power?
        isControl: boolean;     // Is it a switch/sensor?
        controlledDeviceIds?: string[]; // IDs of lights controlled by this switch
    };
}

// ==================== Tool Options ====================

export interface DeviceToolOptions {
    // Type
    category: DeviceCategory;
    familyName: string;
    typeName: string;

    // Configuration
    boxType: number;        // Gang count
    outletType?: OutletType;
    switchType?: SwitchType;

    // Electrical
    voltage: number;
    load: number;

    // Placement
    defaultElevation: number; // mm
    autoHost: boolean;
}

// ==================== Device Tool Class ====================

export class DeviceTool extends BaseTool {
    id = 'device';
    name = 'Device';
    cursor = 'crosshair';

    private currentPoint: THREE.Vector3 | null = null;
    private currentHost: Element | null = null;
    private currentQuat: THREE.Quaternion = new THREE.Quaternion();

    private options: DeviceToolOptions = this.getDefaultOptions();

    private getDefaultOptions(): DeviceToolOptions {
        return {
            category: 'Electrical Fixtures',
            familyName: 'Duplex Receptacle',
            typeName: 'Standard',
            boxType: 1,
            outletType: 'Duplex',
            voltage: 120,
            load: 180, // 180VA standard receptacle
            defaultElevation: 450, // 450mm AFF
            autoHost: true
        };
    }

    /**
     * Set options from UI
     */
    setOptions(options: Partial<DeviceToolOptions>): void {
        this.options = { ...this.options, ...options };

        // Update default elevations based on type
        if (options.category === 'Light Switches' && !options.defaultElevation) {
            this.options.defaultElevation = 1200; // 1200mm switch height
        }
    }

    /**
     * Get current options
     */
    getOptions(): DeviceToolOptions {
        return { ...this.options };
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersectionPoint(event);
        if (!point) return;

        this.createDevice(point, this.currentHost, this.currentQuat);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const point = this.getIntersectionPoint(event);

        if (point) {
            this.currentPoint = point;
            this.updatePreview();
        }
    }

    private getIntersectionPoint(event: ThreeEvent<PointerEvent>): THREE.Vector3 | null {
        if (!this.context) return null;

        if (this.options.autoHost) {
            // Check for walls first (Vertical Face)
            const wallHit = this.getElementIntersection(event, 'Walls');
            if (wallHit) {
                this.currentHost = wallHit.element;

                // Align to wall normal
                // In a real implementation we get the normal from the face
                // Here we assume vertical walls and calculate rotation
                const normal = (wallHit as any).face?.normal || new THREE.Vector3(0, -1, 0);
                const lookAt = new THREE.Vector3().copy(wallHit.point).add(normal);
                this.currentQuat.setFromRotationMatrix(
                    new THREE.Matrix4().lookAt(lookAt, wallHit.point, new THREE.Vector3(0, 0, 1))
                );

                // For vertical faces, we use the default elevation relative to the level
                const levelElevation = this.getLevelElevation(this.currentHost.parameters?.['Base Constraint'] || 'Level 1');
                return new THREE.Vector3(wallHit.point.x, wallHit.point.y, levelElevation + (this.options.defaultElevation / 1000));
            }
        }

        // Fallback to ground plane/level
        this.currentHost = null;
        this.currentQuat.set(0, 0, 0, 1);
        const planeHit = this.getIntersection(event);
        if (planeHit) {
            // On workplane/floor
            const point = this.snapToGrid(planeHit);
            point.z = this.options.defaultElevation / 1000;
            return point;
        }

        return null;
    }

    onPointerUp(): void { }

    private updatePreview() {
        if (!this.context) return;

        if (this.currentPoint) {
            this.context.appStore.setToolPreview({
                type: 'device',
                position: this.currentPoint,
                rotation: new THREE.Euler().setFromQuaternion(this.currentQuat),
                hostId: this.currentHost?.id,
                color: this.getCategoryColor()
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private getCategoryColor(): string {
        switch (this.options.category) {
            case 'Electrical Fixtures': return '#0000FF'; // Blue
            case 'Light Switches': return '#00FF00'; // Green
            case 'Fire Alarm Devices': return '#FF0000'; // Red
            case 'Data Devices': return '#FFA500'; // Orange
            default: return '#808080';
        }
    }

    private createDevice(point: THREE.Vector3, host: Element | null, rotation: THREE.Quaternion) {
        if (!this.context) return;

        // Calculate connector
        const connector: ElectricalConnector = {
            id: uuidv4(),
            systemType: this.options.category === 'Data Devices' ? 'Data' :
                this.options.category === 'Fire Alarm Devices' ? 'Fire Alarm' : 'Power',
            voltage: this.options.voltage,
            phases: 1,
            load: this.options.load * this.options.boxType, // Total load
            loadClassification: this.options.category === 'Light Switches' ? 'Switch' : 'Receptacle'
        };

        const euler = new THREE.Euler().setFromQuaternion(rotation);

        const geometryData: EnhancedDeviceGeometryData = {
            position: point.toArray() as [number, number, number],
            rotation: euler.z * (180 / Math.PI), // Store Z rotation in degrees
            hostId: host?.id,
            mounting: host?.category === 'Walls' ? 'Vertical Face' : 'Face',
            heightFromLevel: this.options.defaultElevation,
            category: this.options.category,
            boxType: this.options.boxType,
            outletType: this.options.outletType,
            switchType: this.options.switchType,
            connectors: [connector],
            symbolRotation: 0
        };

        const categoryMap: Record<DeviceCategory, EnhancedDeviceElement['category']> = {
            'Electrical Fixtures': 'Electrical Fixtures',
            'Light Switches': 'Electrical Fixtures', // BIM puts switches here sometimes or Lighting Devices
            'Data Devices': 'Electrical Fixtures', // Often user-defined
            'Fire Alarm Devices': 'Electrical Devices',
            'Communication Devices': 'Electrical Devices',
            'Nurse Call Devices': 'Electrical Devices'
        };

        const device: EnhancedDeviceElement = {
            id: uuidv4(),
            category: categoryMap[this.options.category] || 'Electrical Fixtures',
            type: `${this.options.familyName}: ${this.options.typeName}`,
            parameters: this.buildParameters(geometryData),
            constraints: this.getDefaultConstraints(host),
            geometryData,
            electricalData: {
                isCircuitLoad: this.options.category !== 'Light Switches', // Switches don't consume power usually (unless smart)
                isControl: this.options.category === 'Light Switches',
                controlledDeviceIds: []
            }
        };

        this.context.bimStore.addElement(device);
    }

    private buildParameters(geom: EnhancedDeviceGeometryData): Record<string, any> {
        return {
            'Elevation': geom.heightFromLevel,
            'Host': geom.hostId || '<None>',
            'Voltage': `${geom.connectors[0].voltage} V`,
            'Apparent Load': `${geom.connectors[0].load} VA`,
            'Number of Poles': 1,
            'Load Classification': geom.connectors[0].loadClassification,
            'Switch Type': geom.switchType || 'N/A',
            'Gang Count': geom.boxType
        };
    }

    private getDefaultConstraints(host: Element | null): Constraint[] {
        const constraints: Constraint[] = [
            // Level constraint could be added here
        ];

        if (host) {
            constraints.push({
                type: 'HOST_CONTAINED',
                targetId: host.id,
                data: {}
            });
        }

        return constraints;
    }
}

// Export types

